import { Controller } from "@hotwired/stimulus";
import { decode } from "js-base64";
import { useHotkeys } from "stimulus-use/hotkeys";
import { MAP_MARKERS } from "../../javascript/constants/map_markers";
import Cookies from "js-cookie";

const KIND_TO_PROP = { "has-done": "hasDone", "wants-to": "wantsTo" };

export default class extends Controller {
  static values = {
    startingPoints: String,
    polylineCoordinates: String,
    startingZoom: Number,
    longitude: Number,
    latitude: Number,
    mapType: { type: String, default: "Standard" },
    draggable: Boolean,
    calloutsEnabled: { type: Boolean, default: true },
    clusterRadius: { type: Number, default: 120 },
    clusterMinPoints: { type: Number, default: 3 },
    clusterMaxZoom: { type: Number, default: 16 },
    userMarksUrl: String,
    pointsUrl: String,
    analyticsSource: String,
  };
  static classes = ["open", "closed"];
  static targets = [
    "map",
    "maximizeButton",
    "minimizeButton",
    "placeMarkerTemplate",
    "gastroMarkerTemplate",
    "clusterTemplate",
    "calloutTemplate",
  ];

  async connect() {
    await fetch("/aon/map/token", {
      method: "GET",
      headers: {
        "Content-Type": "text/plain",
      },
    })
      .then((response) => {
        return response.text();
      })
      .then((data) => {
        this.mapTokenValue = data;
      });

    this.boundCenterOn = this._onCenterOn.bind(this);
    window.addEventListener("aon-map:center", this.boundCenterOn);

    this.initMap();
    if (this._userMarksEnabled()) {
      this._userMarks = {};
      this.boundActivityToggled = this._onActivityButtonToggled.bind(this);
      document.addEventListener("aon-event:activityButtonToggled", this.boundActivityToggled);
    }
  }

  disconnect() {
    window.removeEventListener("aon-map:center", this.boundCenterOn);
    if (this.boundActivityToggled) {
      document.removeEventListener("aon-event:activityButtonToggled", this.boundActivityToggled);
    }
  }

  _userMarksEnabled() {
    return !!this.userMarksUrlValue && Cookies.get("user_signed_in") === "true";
  }

  async setupMapKit() {
    await this._waitForMapKit();

    if (!window.mapKitInitialized) {
      mapkit.init({
        authorizationCallback: (done) => {
          done(this.mapTokenValue);
        },
      });

      window.mapKitInitialized = true;
    }
  }

  _waitForMapKit() {
    if (window.mapkit && window.mapkit.loadedLibraries.length > 0) {
      return Promise.resolve();
    }

    if (!window.__mapkitReady) {
      window.__mapkitReady = new Promise((resolve) => {
        const previous = window.initMapKit;
        window.initMapKit = () => {
          if (typeof previous === "function") previous();
          resolve();
        };
      });
    }

    return window.__mapkitReady;
  }

  async initMap() {
    await this.setupMapKit();

    const module = await import("supercluster");
    this.supercluster = module.default;

    if (this.pointsUrlValue) {
      const response = await fetch(this.pointsUrlValue, { headers: { Accept: "application/json" } });
      const triples = await response.json();
      this.points = triples.map(([id, latitude, longitude]) => ({ id, latitude, longitude }));
    } else {
      this.points = JSON.parse(decode(this.startingPointsValue));
    }

    useHotkeys(this, {
      esc: [this.escapeHandler],
    });

    this.refreshMap();
    if (this._userMarksEnabled()) this._loadUserMarks();
  }

  escapeHandler() {
    if (this.element.classList.contains(...this.openClasses)) {
      this.minimizeMap();
    }
  }

  refreshMap() {
    this.loadSupercluster();
    this.showMap();
  }

  loadSupercluster() {
    const geoJsonPoints = [];

    this.scIndex = new this.supercluster({
      radius: this.clusterRadiusValue,
      maxZoom: this.clusterMaxZoomValue,
      minPoints: this.clusterMinPointsValue,
    });

    this.points.forEach((point) => {
      if (point.latitude && point.longitude) {
        geoJsonPoints.push({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [point.longitude, point.latitude],
          },
          properties: {
            id: point.id,
            title: point.title,
            gastro: point.gastro,
          },
        });
      }
    });

    this.scIndex.load(geoJsonPoints);
  }

  async _loadUserMarks() {
    if (!this._userMarksEnabled()) return;

    const ids = this.scIndex.points.map((point) => point.properties.id);
    if (ids.length === 0) return;

    try {
      const url = `${this.userMarksUrlValue}?streamable_types=Place&ids=${ids.join(",")}`;
      const response = await fetch(url, { headers: { "Accept": "application/json" } });
      const data = await response.json();

      // Merge local _userMarks *on top* of the response from the server so the
      // map also reflects the user's latest clicks.
      this._userMarks = { ...this._normalizeUserMarks(data), ...this._userMarks };
      this._recolorAllAnnotations();
      this._updateProgressCounter();
    } catch (error) {
      console.error("Error loading user marks:", error);
    }
  }

  // Input format from API:
  // {"place_123": ["has_done", "in_list"], "food_234": ["wants_to"], "place_456": ["wants_to"]}
  //
  // Output format:
  // { 123: { "hasDone": true, "wantsTo": false}, 456: { "hasDone": false, "wantsTo": true} }
  _normalizeUserMarks(data) {
    const marks = {};

    Object.entries(data).forEach(([key, kinds]) => {
      if (!key.startsWith("place_")) return;

      const id = Number(key.slice("place_".length));

      marks[id] = {
        hasDone: kinds.includes("has_done"),
        wantsTo: kinds.includes("wants_to"),
      };
    });

    return marks;
  }

  _recolorAllAnnotations() {
    if (!this.bigMap) return;

    this.bigMap.annotations.forEach((annotation) => {
      if (annotation.data.isCluster) return; // We don't recolor clusters

      const img = annotation.element?.querySelector("img");
      if (!img) return;

      img.src = this._markerImageFor(annotation.data.placeId, annotation.data.gastro);
    });
  }

  _markerImageFor(placeId, gastro) {
    const marks = this._userMarks?.[placeId];

    if (marks?.hasDone) return MAP_MARKERS.BEEN_THERE;
    if (marks?.wantsTo) return MAP_MARKERS.WANT_TO_GO;
    return gastro ? MAP_MARKERS.GASTRO : MAP_MARKERS.DEFAULT;
  }

  _onActivityButtonToggled(event) {
    if (!this._userMarksEnabled()) return;

    const { marked, kind, resourceId, resourceType } = event.detail;
    if (resourceType !== "Place") return;

    const id = Number(resourceId);
    // Place must be on this map
    if (!this.scIndex?.points.some((point) => point.properties.id === id)) return;

    // Convert button "kind" from kebab-case to camel
    const prop = KIND_TO_PROP[kind];
    if (!prop) return;

    const marks = this._userMarks[id] ?? { hasDone: false, wantsTo: false };
    this._userMarks[id] = { ...marks, [prop]: marked };

    const annotation = this.bigMap?.annotations.find(
      (a) => a.data.placeId === id
    );

    // Set new image if necessary
    if (annotation) {
      const img = annotation.element?.querySelector("img");
      if (img) img.src = this._markerImageFor(id, annotation.data.gastro);
    }

    this._updateProgressCounter();
  }

  _updateProgressCounter() {
    // The controller is bound to the map, but the counter lives outside the map,
    // so we look for it explicitly in the DOM.
    const container = document.querySelector("[data-user-marks-progress]");
    if (!container) return;

    let been = 0;
    let want = 0;
    Object.values(this._userMarks).forEach((marks) => {
      if (marks.hasDone) been++;
      if (marks.wantsTo) want++;
    });

    container.querySelector("[data-user-marks-been-count]").textContent = been;
    container.querySelector("[data-user-marks-want-count]").textContent = want;

    // The tracker doubles as the map legend: its icons are the marker art
    // itself, so they can't drift from what's on the map.
    const beenIcon = container.querySelector("[data-user-marks-been-icon]");
    const wantIcon = container.querySelector("[data-user-marks-want-icon]");
    if (beenIcon) beenIcon.src = MAP_MARKERS.BEEN_THERE;
    if (wantIcon) wantIcon.src = MAP_MARKERS.WANT_TO_GO;

    container.hidden = false;
  }

  _equals(a, b) {
    if (a === b) return true;

    if (a instanceof Date && b instanceof Date)
      return a.getTime() === b.getTime();

    if (!a || !b || (typeof a !== "object" && typeof b !== "object"))
      return a === b;

    if (a.prototype !== b.prototype) return false;

    const keys = Object.keys(a);
    if (keys.length !== Object.keys(b).length) return false;

    return keys.every((k) => this._equals(a[k], b[k]));
  }

  _annotationId(c) {
    return c.properties.cluster_id
      ? `c-${c.properties.cluster_id}`
      : `p-${c.properties.id}`;
  }

  // During a gesture, off-screen markers are only removed once it ends, to
  // avoid DOM churn mid-pan; a whole-zoom-level change still prunes right
  // away because the cluster set changes and old clusters would overlap.
  annotateMap(bigMap, zoomLevel, { duringGesture = false } = {}) {
    const bounds = bigMap.region.toBoundingRegion();
    const east =
      bounds.eastLongitude < bounds.westLongitude
        ? bounds.eastLongitude + 360 // region crosses the antimeridian
        : bounds.eastLongitude;
    // Pad a little so markers are ready just before they scroll into view.
    const latPad = (bounds.northLatitude - bounds.southLatitude) * 0.1;
    const lngPad = (east - bounds.westLongitude) * 0.1;
    const newClusters = this.scIndex.getClusters(
      [
        bounds.westLongitude - lngPad,
        bounds.southLatitude - latPad,
        east + lngPad,
        bounds.northLatitude + latPad,
      ],
      zoomLevel
    );

    const wantedIds = new Set(newClusters.map((c) => this._annotationId(c)));
    const currentIds = new Set(bigMap.annotations.map((a) => a.data.annotationId));

    // **** ADD ANNOTATIONS ****
    const clustersToAdd = newClusters.filter((c) => !currentIds.has(this._annotationId(c)));
    if (clustersToAdd.length > 0) {
      bigMap.addAnnotations(clustersToAdd.map((c) => this._createAnnotation(c)));
    }

    // **** REMOVE ANNOTATIONS ****
    // Drop anything that's off screen or replaced at this zoom, so panning
    // doesn't pile up markers. Keep the selected one so its callout stays open.
    const clusterZoom = Math.floor(zoomLevel);
    if (duringGesture && clusterZoom === this.prunedZoom) return;
    this.prunedZoom = clusterZoom;
    const annosToRemove = bigMap.annotations.filter(
      (a) => !wantedIds.has(a.data.annotationId) && a !== bigMap.selectedAnnotation
    );
    if (annosToRemove.length > 0) bigMap.removeAnnotations(annosToRemove);
  }

  _getMarkerType(cluster) {
    // For non-place clusters, we don't need a marker type
    if (cluster.properties.point_count) {
      return null;
    }

    return this._markerImageFor(cluster.properties.id, cluster.properties.gastro);
  }

  _createAnnotation(cluster) {
    if (cluster.properties.point_count) {
      const coord = new mapkit.Coordinate(
        parseFloat(cluster.geometry.coordinates[1]),
        parseFloat(cluster.geometry.coordinates[0])
      );

      const node = this.clusterTemplateTarget.content.firstElementChild.cloneNode(
        true
      );
      const count = cluster.properties.point_count.toString();
      const circleSize = 26 + Math.min(22, cluster.properties.point_count / 10);

      let fontSize = 11;
      switch (count.length) {
        case 1:
          fontSize = 11;
          break;
        case 2:
          fontSize = 12;
          break;
        case 3:
          fontSize = 13;
          break;
        case 4:
          fontSize = 14;
          break;
        default:
          fontSize = 11;
      }

      node.querySelector(".cluster-count").innerText = count;
      node.style.height = `${circleSize}px`;
      node.style.width = `${circleSize}px`;
      node.style.fontSize = `${fontSize}px`;

      node.dataset.latitude = coord.latitude;
      node.dataset.longitude = coord.longitude;

      const clusterAnnotation = new mapkit.Annotation(coord, () => node, {
        animates: false,
        data: {
          annotationId: `c-${cluster.id}`,
          clusterId: cluster.id,
          isCluster: true,
        },
      });

      return clusterAnnotation;
    } else {
      const coord = new mapkit.Coordinate(
        parseFloat(cluster.geometry.coordinates[1]),
        parseFloat(cluster.geometry.coordinates[0])
      );

      let calloutDelegate = this.calloutsEnabledValue
        ? {
            calloutElementForAnnotation: (annotation) => {
              return this.fetchCallout(cluster.properties.id);
            },
            calloutAnchorOffsetForAnnotation: (annotation, size) => {
              return new DOMPoint(0, -(size["height"] + 40));
            },
            calloutShouldAnimateForAnnotation: (annotation) => {
              return false;
            },
          }
        : null;

      let node;
      const markerType = this._getMarkerType(cluster);

      if (markerType === MAP_MARKERS.GASTRO) {
        node = this.gastroMarkerTemplateTarget.content.firstElementChild.cloneNode(
          true
        );
      } else {
        node = this.placeMarkerTemplateTarget.content.firstElementChild.cloneNode(
          true
        );
      }
      node.querySelector("img").src = markerType;

      node.setAttribute("title", cluster.properties.title);

      const childAnnotation = new mapkit.Annotation(coord, () => node, {
        animates: false,
        callout: calloutDelegate,
        data: {
          annotationId: `p-${cluster.properties.id}`,
          isCluster: false,
          placeId: cluster.properties.id,
          gastro: cluster.properties.gastro,
        },
      });

      return childAnnotation;
    }
  }

  fetchCallout(placeId) {
    const node = this.calloutTemplateTarget.content.firstElementChild.cloneNode(
      true
    );
    let url = node.dataset.url.replace("ID", placeId);

    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        this.dispatchGaEventOnPinClick(placeId);
        node.innerHTML = data.html;
        this._trackCallout(node, placeId);
      });

    return node;
  }

  _trackCallout(node, placeId) {
    if (!this.analyticsSourceValue || !window.posthog?.capture) return;

    const properties = {
      source: this.analyticsSourceValue,
      place_id: placeId,
      place_title: node.querySelector(".callout--title")?.textContent.trim(),
    };
    window.posthog.capture("map_place_opened", properties);

    // The place link, not the Been There / Want to Go / list buttons beside it.
    node.addEventListener("click", (event) => {
      if (!event.target.closest(".callout-component > a")) return;
      window.posthog.capture("map_place_clicked", properties, { transport: "sendBeacon" });
    });
  }

  dispatchGaEventOnPinClick(placeId) {
    this.dispatch("userEvent", {
      prefix: "analytics",
      detail: {
        handler: "handleBasicGaEvent",
        data: {
          gaAction: "Clicked Pin",
          gaCategory: "Dynamic Map",
          gaLabel: "@PATH@",
        },
      },
    });
  }

  _mapkitMapType() {
    switch (this.mapTypeValue) {
      case "standard":
        return mapkit.Map.MapTypes.Standard;
      case "hybrid":
        return mapkit.Map.MapTypes.Hybrid;
      case "satellite":
        return mapkit.Map.MapTypes.Satellite;
      case "muted-standard":
        return mapkit.Map.MapTypes.MutedStandard;
      default:
        return mapkit.Map.MapTypes.Standard;
    }
  }

  showMap() {
    this.mapTarget.innerHTML = "";

    const coords = this.scIndex.points.map((p) => {
      let c = p.geometry.coordinates;
      return { latitude: parseFloat(c[1]), longitude: parseFloat(c[0]) };
    });

    const opts = {
      mapType: this._mapkitMapType(),
      isScrollEnabled: this.draggableValue,
      padding: new mapkit.Padding({ top: 14, right: 14, bottom: 14, left: 14 }),
      showsMapTypeControl: false,
      pointOfInterestFilter: mapkit.PointOfInterestFilter.including([
        "Aquarium",
        "Hotel",
        "Library",
        "Museum",
        "NationalPark",
        "Park",
        "Zoo",
      ]),
    };

    if (coords.length === 0) {
      // No points: use the provided fallback lat/long and zoom
      opts.center = new mapkit.Coordinate(
        this.latitudeValue,
        this.longitudeValue
      );
      this.bigMap = new mapkit.Map(this.mapTarget, opts);
      this.bigMap._impl.zoomLevel = this.startingZoomValue;
    } else if (coords.length === 1) {
      // Single point: center on it at the provided zoom level
      opts.center = new mapkit.Coordinate(
        coords[0].latitude,
        coords[0].longitude
      );
      this.bigMap = new mapkit.Map(this.mapTarget, opts);
      this.bigMap._impl.zoomLevel = this.startingZoomValue;
    } else {
      const deltas = this._calcDeltas(coords);

      // Add padding to ensure all markers are visible (multiply deltas by 1.2 for 20% padding)
      const paddingMultiplier = 1.2;
      const span = new mapkit.CoordinateSpan(
        Math.max(deltas.latitudeDelta * paddingMultiplier, 0.01), // Ensure minimum delta
        Math.max(deltas.longitudeDelta * paddingMultiplier, 0.01)
      );

      const center = new mapkit.Coordinate(deltas.latitude, deltas.longitude);
      const region = new mapkit.CoordinateRegion(center, span);

      opts.region = region;

      // clear event listeners
      if (this.bigMap) this._clearMapEventListeners();

      // create new map
      this.bigMap = new mapkit.Map(this.mapTarget, opts);
    }

    this._setMapEventListeners();

    this._addPolyline();

    this.annotateMap(this.bigMap, this._zoomLevel());
  }

  _addPolyline() {
    // Only render if polyline_coordinates were explicitly provided with actual data
    // When using route_66_polyline controller, this should be empty and we skip rendering
    if (!this.polylineCoordinatesValue || this.polylineCoordinatesValue === "") {
      return;
    }

    try {
      const decoded = decode(this.polylineCoordinatesValue);
      const coordinates = JSON.parse(decoded);
      
      // Skip if empty array (default value) or no coordinates
      // This prevents rendering fallback polylines
      // Also check if decoded value is just "[]" (empty array JSON)
      if (!coordinates || !Array.isArray(coordinates) || coordinates.length === 0 || decoded === "[]") {
        return;
      }

      this.addPolylineFromCoordinates(coordinates);
    } catch (error) {
      // Silently fail - don't render fallback
      return;
    }
  }

  addPolylineFromCoordinates(coordinates) {
    if (!this.bigMap) {
      console.warn("Map not initialized yet");
      return;
    }

    if (!coordinates || coordinates.length === 0) {
      return;
    }

    const mapkitCoordinates = coordinates.map((coord) => {
      return new mapkit.Coordinate(
        parseFloat(coord.latitude),
        parseFloat(coord.longitude)
      );
    });

    const polylineOverlay = new mapkit.PolylineOverlay(mapkitCoordinates, {
      style: new mapkit.Style({
        strokeColor: "#E63946", // Route 66 red color
        lineWidth: 2,
        lineJoin: "round",
        lineCap: "round",
      }),
    });

    this.bigMap.addOverlay(polylineOverlay);
  }

  _setMapEventListeners() {
    this.bigMap.addEventListener("region-change-start", (ev) => {
      const boundHeartbeat = function () {
        let map = ev.target;

        this.annotateMap(map, this._zoomLevel(), { duringGesture: true });
      }.bind(this);

      this.mapChangeHeartbeat = setInterval(boundHeartbeat, 50);
    });

    this.bigMap.addEventListener("region-change-end", () => {
      this.annotateMap(this.bigMap, this.bigMap._impl.zoomLevel);

      clearInterval(this.mapChangeHeartbeat);
    });

    // Timing diagnostic: log pointerdown -> pointerup -> click -> select to see
    // if MapKit's tap recognizer is rejecting events because the gesture took
    // too long or moved between down and up. Captured at document level.
    let downAt = null;
    let downXY = null;
    const logEv = (label, ev) => {
      const now = Math.round(performance.now());
      const dt = downAt != null ? now - downAt : null;
      const dx = downXY ? ev.clientX - downXY[0] : null;
      const dy = downXY ? ev.clientY - downXY[1] : null;
      const slot = (ev.composedPath?.() || []).find(
        (n) => n.nodeType === 1 && n.getAttribute?.("slot")?.startsWith?.("mk-slot-")
      );
      console.log(
        `[DynamicMap] ${label} t=${now} dtFromDown=${dt} dx=${dx} dy=${dy} x=${ev.clientX} y=${ev.clientY} target=${ev.target?.tagName} slot=${slot?.getAttribute?.("slot") || "-"} title=${slot?.getAttribute?.("title") || "-"}`
      );
    };
    document.addEventListener(
      "pointerdown",
      (ev) => {
        if (!ev.composedPath?.().includes(this.element)) return;
        downAt = Math.round(performance.now());
        downXY = [ev.clientX, ev.clientY];
        logEv("pointerdown", ev);
      },
      true
    );
    document.addEventListener(
      "pointerup",
      (ev) => {
        if (!ev.composedPath?.().includes(this.element)) return;
        logEv("pointerup", ev);
      },
      true
    );
    document.addEventListener(
      "click",
      (ev) => {
        if (!ev.composedPath?.().includes(this.element)) return;
        logEv("click", ev);
      },
      true
    );

    this.bigMap.addEventListener("select", (event) => {
      let annotation = event.annotation;
      console.log(
        `[DynamicMap] SELECT t=${Math.round(performance.now())} isCluster=${annotation.data.isCluster} id=${annotation.data.annotationId}`
      );

      if (annotation.data.isCluster) {
        let expZoomLevel = this.scIndex.getClusterExpansionZoom(
          annotation.data.clusterId
        );

        let coord = new mapkit.Coordinate(
          annotation.coordinate.latitude,
          annotation.coordinate.longitude
        );

        this._centerAndZoomTo(coord, expZoomLevel);
      } else {
        this._enlargeMarker(annotation);
      }
    });

    this.bigMap.addEventListener("deselect", (event) => {
      const annotation = event.annotation;

      if (!annotation.data.isCluster) {
        annotation.element.classList.remove("scale-125");
      }
    });
  }

  // Moves the map to a region requested by another part of the page, e.g. the
  // all-places search bar. detail: { latitude, longitude, latitudeDelta, longitudeDelta }
  // If no point falls in that region, it widens around the same center until
  // the nearest point is in view, so a search never lands on an empty map.
  _onCenterOn(event) {
    if (!this.bigMap) return;

    const { latitude, longitude } = event.detail;
    let { latitudeDelta, longitudeDelta } = event.detail;

    const offsets = this.points.map((p) => ({
      lat: Math.abs(p.latitude - latitude),
      // Shortest way around the antimeridian.
      lng: Math.abs(((p.longitude - longitude + 540) % 360) - 180),
    }));
    const inView = offsets.some((o) => o.lat <= latitudeDelta / 2 && o.lng <= longitudeDelta / 2);

    if (!inView && offsets.length > 0) {
      const nearest = offsets.reduce((a, b) => (a.lat ** 2 + a.lng ** 2 <= b.lat ** 2 + b.lng ** 2 ? a : b));
      // Scale both spans together to keep the shape, with 20% room so the
      // marker isn't pinned to the edge.
      const scale = Math.max(nearest.lat / (latitudeDelta / 2), nearest.lng / (longitudeDelta / 2)) * 1.2;
      latitudeDelta = Math.min(latitudeDelta * scale, 180);
      longitudeDelta = Math.min(longitudeDelta * scale, 360);
    }

    const region = new mapkit.CoordinateRegion(
      new mapkit.Coordinate(latitude, longitude),
      new mapkit.CoordinateSpan(latitudeDelta, longitudeDelta)
    );
    this.bigMap.setRegionAnimated(region, true);
  }

  _clearMapEventListeners() {
    this.bigMap.removeEventListener("region-change-start");
    this.bigMap.removeEventListener("region-change-end");
    this.bigMap.removeEventListener("select");
    this.bigMap.removeEventListener("deselect");
  }

  _enlargeMarker(annotation) {
    annotation.element.classList.add("scale-125");
  }

  _zoomLevel() {
    return this.bigMap._impl.zoomLevel;
  }

  _centerAndZoomTo(coord, level) {
    // Ensure the coord is a valid mapkit.Coordinate object
    if (!(coord instanceof mapkit.Coordinate)) {
      coord = new mapkit.Coordinate(coord.latitude, coord.longitude);
    }

    this.bigMap.setCenterAnimated(coord, true);
    if (level > this.bigMap._impl.zoomLevel)
      this.bigMap._impl.zoomLevel = level;
  }

  _calcDeltas(points) {
    // points should be an array of { latitude: X, longitude: Y }
    let minX,
      maxX,
      minY,
      maxY;

      // init first point
    ((point) => {
      minX = point.latitude;
      maxX = point.latitude;
      minY = point.longitude;
      maxY = point.longitude;
    })(points[0]);

    // calculate rect
    points.map((point) => {
      minX = Math.min(minX, point.latitude);
      maxX = Math.max(maxX, point.latitude);
      minY = Math.min(minY, point.longitude);
      maxY = Math.max(maxY, point.longitude);
    });

    const midX = (minX + maxX) / 2;
    const midY = (minY + maxY) / 2;
    const deltaX = maxX - minX;
    const deltaY = maxY - minY;

    return {
      latitude: midX,
      longitude: midY,
      latitudeDelta: deltaX,
      longitudeDelta: deltaY,
    };
  }

  maximizeMap() {
    let container = this.element;

    container.classList.remove(...this.closedClasses);
    container.classList.add(...this.openClasses);

    this.maximizeButtonTarget.classList.add("hidden");
    this.minimizeButtonTarget.classList.remove("hidden");
  }

  minimizeMap() {
    let container = this.element;

    container.classList.remove(...this.openClasses);
    container.classList.add(...this.closedClasses);

    this.maximizeButtonTarget.classList.remove("hidden");
    this.minimizeButtonTarget.classList.add("hidden");
  }
}
